import React from 'react';
import { DollarSign, Sparkles, FileSearch, RotateCcw, Search, X, LogOut, UserPlus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { SettingsModal } from './SettingsModal';
import { VideoCarouselButton } from './VideoCarouselModal';
import { useAuth } from '../contexts/AuthContext';

const VIUBUX_LOGO = 'https://customer-assets.emergentagent.com/job_libro-quote-gen/artifacts/pf9umyn8_5.png';

export function HeaderBar({
  savedQuoteCode,
  showRecoveryInput,
  setShowRecoveryInput,
  recoveryCode,
  setRecoveryCode,
  handleRecoverQuote,
  isRecovering,
  currency,
  toggleCurrency,
  onShowVideoModal,
  settings,
  onSaveSettings,
  settingsOpen,
  setSettingsOpen,
  showDiscounts,
  setShowDiscounts,
  customDiscount,
  setCustomDiscount,
  customDiscountType,
  setCustomDiscountType,
  onLoadQuote,
  referrerCode,
  referrerName,
  showReferralInput,
  setShowReferralInput,
  referralInputValue,
  setReferralInputValue,
  verifyReferral,
  clearReferral,
  isVerifyingReferral
}) {
  const { user, logout } = useAuth();
  
  return (
    <header className="border-b border-white/10 bg-[#0f133a]/80 backdrop-blur-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={VIUBUX_LOGO} alt="Viubux" className="h-8 object-contain" />
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <Sparkles className="w-5 h-5 text-[#aa2aff]" />
                Cotizador de Servicios Audiovisuales
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Active Quote Code Badge */}
            {savedQuoteCode && !showRecoveryInput && (
              <div 
                className="flex items-center gap-1.5 bg-[#aa2aff]/15 border border-[#aa2aff]/30 rounded-lg px-3 py-1 cursor-pointer hover:bg-[#aa2aff]/25 transition-colors"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(savedQuoteCode);
                    toast.success('Código copiado al portapapeles');
                  } catch (e) {
                    const ta = document.createElement('textarea');
                    ta.value = savedQuoteCode;
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                    toast.success('Código copiado');
                  }
                }}
                title="Click para copiar"
                data-testid="badge-quote-code"
              >
                <span className="text-xs text-[#aa2aff]/70">N°</span>
                <span className="text-sm font-mono font-bold text-[#aa2aff]">{savedQuoteCode}</span>
              </div>
            )}
            
            {/* Recovery Code Input */}
            {showRecoveryInput ? (
              <div className="flex items-center gap-2 bg-white/5 rounded-lg px-2 py-1 border border-white/10">
                <Input
                  placeholder="VBX-XXXXXX"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  className="w-32 h-8 bg-transparent border-0 text-white text-sm font-mono placeholder:text-slate-500 focus-visible:ring-0"
                  data-testid="input-recovery-code"
                  onKeyDown={(e) => e.key === 'Enter' && handleRecoverQuote()}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRecoverQuote}
                  disabled={isRecovering}
                  className="h-7 px-2 text-[#aa2aff] hover:text-white hover:bg-[#aa2aff]/20"
                  data-testid="btn-recover-quote"
                >
                  {isRecovering ? <RotateCcw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setShowRecoveryInput(false); setRecoveryCode(''); }}
                  className="h-7 px-2 text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRecoveryInput(true)}
                className="text-slate-400 hover:text-white hover:bg-white/10 gap-1"
                data-testid="btn-show-recovery"
              >
                <FileSearch className="w-4 h-4" />
                <span className="hidden sm:inline">Recuperar</span>
              </Button>
            )}

            {/* Referral Badge (validated) */}
            {referrerCode && !showReferralInput && (
              <div
                className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-1"
                title={`Referido: ${referrerName || referrerCode}`}
                data-testid="badge-referrer"
              >
                <Check className="w-3 h-3 text-emerald-400" />
                <span className="text-xs text-emerald-300 font-medium max-w-[120px] truncate">{referrerName || referrerCode}</span>
                <button
                  onClick={clearReferral}
                  className="text-emerald-300/60 hover:text-emerald-200 ml-0.5"
                  title="Quitar referido"
                  data-testid="btn-clear-referrer"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* Referral Code Input */}
            {showReferralInput ? (
              <div className="flex items-center gap-2 bg-white/5 rounded-lg px-2 py-1 border border-white/10">
                <Input
                  placeholder="Usuario o correo del referido"
                  value={referralInputValue}
                  onChange={(e) => setReferralInputValue(e.target.value)}
                  onBlur={() => referralInputValue.trim() && verifyReferral(referralInputValue.trim())}
                  className="w-44 h-8 bg-transparent border-0 text-white text-sm placeholder:text-slate-500 focus-visible:ring-0"
                  data-testid="input-referrer-code"
                  onKeyDown={(e) => e.key === 'Enter' && verifyReferral(referralInputValue.trim())}
                  autoFocus
                />
                {isVerifyingReferral ? (
                  <RotateCcw className="w-4 h-4 animate-spin text-[#aa2aff]" />
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => verifyReferral(referralInputValue.trim())}
                    disabled={!referralInputValue.trim()}
                    className="h-7 px-2 text-[#aa2aff] hover:text-white hover:bg-[#aa2aff]/20"
                    data-testid="btn-verify-referrer"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setShowReferralInput(false); setReferralInputValue(''); }}
                  className="h-7 px-2 text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              !referrerCode && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowReferralInput(true)}
                  className="text-slate-400 hover:text-white hover:bg-white/10 gap-1"
                  data-testid="btn-show-referrer"
                >
                  <UserPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">Referido</span>
                </Button>
              )
            )}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleCurrency}
              className="text-slate-400 hover:text-white hover:bg-white/10 gap-1"
              data-testid="btn-toggle-currency"
            >
              <DollarSign className="w-4 h-4" />
              {currency}
            </Button>
            <VideoCarouselButton onClick={onShowVideoModal} />
            <SettingsModal
              settings={settings}
              onSave={onSaveSettings}
              isOpen={settingsOpen}
              onOpenChange={setSettingsOpen}
              showDiscounts={showDiscounts}
              onToggleDiscounts={setShowDiscounts}
              customDiscount={customDiscount}
              onCustomDiscountChange={setCustomDiscount}
              customDiscountType={customDiscountType}
              onCustomDiscountTypeChange={setCustomDiscountType}
              onLoadQuote={onLoadQuote}            />
            {user && (
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                data-testid="btn-logout"
                title={user.email}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
